// CacheShare cascade taxonomy.
//
// Single source of truth for the seven-step search flow:
//   Sport -> Tier -> Region -> Governing Body -> League/Circuit
//          -> Conference (NCAA only) -> Team
//
// Every leaf the user can pick (governing body, sub-division, conference)
// resolves to a `dataSource` + a list of `cerebroSports` (Cerebro sport-keys
// to query) and an optional `leagueNames` filter that narrows results inside
// an umbrella collection (intlmb / intlwb).
//
// Values for which the cache backend has no data yet are marked `coming`
// so the UI can render them disabled rather than producing empty results.
//
// `coming` items still need new ingest pipelines (each requires a new
// CEREBRO_SPORT_CONFIG entry in cerebro_ingest.py + a new cache_cb_*
// collection):
//   College tier:      NAIA, NJCAA, JUCO, CCAA  (need SR or CB ingest)
//                      NCAA Division II / III   (SR ingest only does D-I)
//   HS / Grassroots:   Adidas 3SSB (15U / 16U / 17U / Girls / U18)
//                      Under Armour Association (15U / 16U / 17U + Girls)
//                      OTE (Overtime Elite)
//                      State HS Association (50 states + DC)
//                      Elite Showcase, Scouting / Exposure
//                      EYBL EYCL, EYBL Scholastic
//   HS / Grassroots international: FIBA Europe / Americas / Asia /
//                      Africa / Oceania Youth, National Youth Federation
//
// Anything in the spec not in that list now routes to a real cached
// collection (after the admin re-runs the relevant Cerebro ingest job for
// `intlmb` / `intlwb`).

// Base sports. Gender (Men/Boys vs Women/Girls) is picked separately in
// Step 1's gender row when `hasGender` is true, so this list stays tight.
export const SPORTS = [
  { value: "bb", name: "Basketball", status: "live", hasGender: true },
  // Football is men-only (Genius Sports NCAA feed), so the gender step is
  // skipped — hasGender: false short-circuits the Step 1 gender row.
  { value: "fb", name: "Football", status: "live", hasGender: false },
  { value: "vb", name: "Volleyball", status: "coming", hasGender: true },
  { value: "soc", name: "Soccer", status: "coming", hasGender: true },
];

export const GENDERS = [
  { value: "M", name: "Men/Boys" },
  { value: "F", name: "Women/Girls" },
];

// Compose a (sport, gender) pick into the legacy backend sport-key the rest
// of the app expects (mbb/wbb/fb/...). Gender-less sports return their bare
// sport value unchanged.
export const legacySportKey = (sportValue, gender) => {
  if (sportValue === "bb") return gender === "F" ? "wbb" : "mbb";
  return sportValue;
};

export const TIERS = [
  { value: "college", name: "College" },
  { value: "pro", name: "Pro" },
  { value: "hs", name: "HS / Grassroots" },
];

// Region picker is hidden for College. Pro and HS use the same 6 regions
// per spec — no extras.
const SIX_REGIONS = [
  { value: "na", name: "North America" },
  { value: "eu", name: "Europe" },
  { value: "ap", name: "Asia-Pacific" },
  { value: "lat", name: "Americas (LatAm)" },
  { value: "amena", name: "Africa / MENA" },
  { value: "oce", name: "Oceania" },
];
export const REGIONS_BY_TIER = {
  pro: SIX_REGIONS,
  hs: SIX_REGIONS,
};
// Backwards-compat alias for callers that still consume a flat REGIONS list.
export const REGIONS = SIX_REGIONS;

// Legacy helper kept for callers that still pass the combined sport-key.
// Prefer reading `filters.gender` directly when you have it.
export const cerebroGenderForSport = (sportValue) => {
  if (sportValue === "mbb") return "M";
  if (sportValue === "wbb") return "F";
  return null;
};

// ---------------------------------------------------------------------------
// Governing bodies, indexed by tier and (for Pro/HS) region.
//
// Each body carries:
//   - `name`           label rendered in the dropdown
//   - `status`         "live" or "coming" (renders disabled)
//   - `requiresHsGate` true for OTE and State HS Association — the spec
//                      requires both stay locked behind HS/Grassroots, never
//                      surfacing in college / pro searches
//   - `dataSource`     "cerebro" | "sportsradar"
//   - `cerebroSports`  list of cerebro sport-keys to query (omit for SR-only)
//   - `leagueNames`    optional list of leagueName values that narrow results
//                      inside an umbrella cerebro collection (intlmb/intlwb)
//   - `subDivisions`   optional sub-division dropdown (Step 5)
// ---------------------------------------------------------------------------

// NCAA body — D-I (men's + women's) goes through LIVE SportRadar so coaches
// see current-season rosters / stats. D-II and D-III stay on Cerebro because
// SR's NCAAMB / NCAAWB endpoints don't cover them.
//
// The Power 4 / G5 / HBCU / FCS sub-divisions are D-I conference subsets;
// the `conferenceFilter` arrays narrow the picker against the `conference`
// field on each SR team doc. CIAA / SIAC inside the HBCU group are D-II,
// so resolveFetchPlan auto-routes those single-conference picks to the
// D-II Cerebro cache (see `conferenceForcesD2` branch).
// NCAA Football — routed through the Genius Sports Warehouse REST API
// (rosters + per-player season stats). `sports: ["fb"]` scopes this body to
// the Football cascade; the basketball bodies below carry no `sports` tag
// and default to basketball-only.
const NCAA_FOOTBALL_BODY = {
  value: "ncaa_fb",
  name: "NCAA",
  status: "live",
  dataSource: "genius",
  sports: ["fb"],
};

const NCAA_BODY = {
  value: "ncaa",
  name: "NCAA",
  status: "live",
  dataSource: "sportsradar",
  // byGender no longer carries cerebroSports for D-I — resolveFetchPlan
  // derives `srSport` from `resolvedGender` directly in the SR branch.
  // (D-II / D-III sub-divisions still declare cerebroSports below, which
  // is the override that flips those picks back to Cerebro.)
  subDivisions: [
    {
      value: "d1_p4", name: "Division I, Power 4", status: "live",
      // No cerebroSports here — let the parent body's dataSource:sportsradar
      // pass through. conferenceFilter narrows the SR team list client-side.
      conferenceFilter: ["ACC", "Big Ten", "Big 12", "SEC"],
    },
    {
      value: "d1_g5", name: "Division I, G5", status: "live",
      conferenceFilter: ["American", "C-USA", "MAC", "Mountain West", "Sun Belt"],
    },
    {
      value: "d1_hbcu", name: "Division I, HBCU", status: "live",
      conferenceFilter: ["MEAC", "SWAC", "CIAA", "SIAC"],
    },
    {
      value: "d1_fcs", name: "Division I, FCS", status: "live",
      // FCS is football-specific; NCAA D-I basketball has no FCS analog,
      // so this returns the full D-I team list when picked. Listed for
      // spec parity with the Step 5 NCAA dropdown.
      conferenceFilter: [],
    },
    {
      // D-II overrides to Cerebro via cerebroSports — SR has no D-II coverage.
      value: "d2", name: "Division II", status: "live",
      byGender: {
        M: { cerebroSports: ["ncaad2mb"] },
        F: { cerebroSports: ["ncaad2wb"] },
      },
    },
    {
      // D-III same — overrides to Cerebro.
      value: "d3", name: "Division III", status: "live",
      byGender: {
        M: { cerebroSports: ["ncaad3mb"] },
        F: { cerebroSports: ["ncaad3wb"] },
      },
    },
  ],
};

// NCAA conferences shown in Step 6 (optional). Selecting one narrows the team
// list client-side via the `conference` field on each SR team doc.
//
// `division` tags whether the conference plays in D-I, D-II, or D-III. The
// HBCU group spans both: MEAC + SWAC are D-I, CIAA + SIAC are D-II. The
// cascade uses this tag to route to the right cache (SR mbb for D-I,
// Cerebro ncaad2mb for D-II) so a CIAA pick doesn't return zero teams.
export const NCAA_CONFERENCE_GROUPS = [
  {
    label: "Power 4",
    options: [
      { value: "ACC", name: "ACC", division: "d1" },
      { value: "Big Ten", name: "Big Ten", division: "d1" },
      { value: "Big 12", name: "Big 12", division: "d1" },
      { value: "SEC", name: "SEC", division: "d1" },
    ],
  },
  {
    label: "G5",
    options: [
      { value: "American", name: "AAC", division: "d1" },
      { value: "C-USA", name: "C-USA", division: "d1" },
      { value: "MAC", name: "MAC", division: "d1" },
      { value: "Mountain West", name: "Mountain West", division: "d1" },
      { value: "Sun Belt", name: "Sun Belt", division: "d1" },
    ],
  },
  {
    label: "HBCU",
    options: [
      { value: "MEAC", name: "MEAC", division: "d1" },
      { value: "SWAC", name: "SWAC", division: "d1" },
      { value: "CIAA", name: "CIAA", division: "d2" },
      { value: "SIAC", name: "SIAC", division: "d2" },
    ],
  },
  {
    label: "Other DI",
    options: [
      { value: "ASUN", name: "ASUN", division: "d1" },
      { value: "Atlantic 10", name: "Atlantic 10", division: "d1" },
      { value: "Big East", name: "Big East", division: "d1" },
      { value: "Big South", name: "Big South", division: "d1" },
      { value: "Big West", name: "Big West", division: "d1" },
      { value: "CAA", name: "CAA", division: "d1" },
      { value: "Horizon", name: "Horizon", division: "d1" },
      { value: "Metro Atlantic", name: "Metro Atlantic", division: "d1" },
      { value: "Missouri Valley", name: "Missouri Valley", division: "d1" },
      { value: "NEC", name: "NEC", division: "d1" },
      { value: "Northeast", name: "Northeast", division: "d1" },
      { value: "Ohio Valley", name: "OVC", division: "d1" },
      { value: "Patriot", name: "Patriot", division: "d1" },
      { value: "Southern", name: "Southern", division: "d1" },
      { value: "Southland", name: "Southland", division: "d1" },
      { value: "Summit", name: "Summit", division: "d1" },
      { value: "WAC", name: "WAC", division: "d1" },
      { value: "WCC", name: "WCC", division: "d1" },
    ],
  },
];

// Lookup helper: conference value -> {division, name, group}
const CONFERENCE_INDEX = (() => {
  const idx = {};
  for (const group of NCAA_CONFERENCE_GROUPS) {
    for (const opt of group.options) {
      idx[opt.value] = { ...opt, group: group.label };
    }
  }
  return idx;
})();
export const conferenceMeta = (conferenceValue) =>
  conferenceValue ? CONFERENCE_INDEX[conferenceValue] || null : null;

// EYBL governing body is gender-aware: M dropdowns surface boys age groups;
// W dropdowns surface girls age groups. The `cerebroSports` list is what the
// frontend hits — each maps to one cache_cb_* collection.
const EYBL_BODY = {
  value: "nike_eybl",
  name: "Nike EYBL",
  status: "live",
  dataSource: "cerebro",
  byGender: {
    M: {
      subDivisions: [
        { value: "eyblmb", name: "17U", status: "live", cerebroSports: ["eyblmb"] },
        { value: "eybl16mb", name: "16U", status: "live", cerebroSports: ["eybl16mb"] },
        { value: "eybl15mb", name: "15U", status: "live", cerebroSports: ["eybl15mb"] },
        { value: "eybl_eycl", name: "EYCL", status: "live", cerebroSports: ["eybleyclmb"] },
        { value: "eybl_scholastic", name: "Scholastic", status: "live", cerebroSports: ["eyblschmb"] },
      ],
      // Default when the user has not picked a sub-division: union of all live
      // boys EYBL caches so a single search returns every grassroots boys team.
      cerebroSports: ["eyblmb", "eybl16mb", "eybl15mb", "eybleyclmb", "eyblschmb"],
    },
    F: {
      subDivisions: [
        { value: "eyblwb", name: "Girls 17U", status: "live", cerebroSports: ["eyblwb"] },
        { value: "eybl16wb", name: "Girls 16U", status: "live", cerebroSports: ["eybl16wb"] },
        { value: "eybl15wb", name: "Girls 15U", status: "live", cerebroSports: ["eybl15wb"] },
        { value: "eybl_eycl_w", name: "EYCL", status: "live", cerebroSports: ["eybleyclwb"] },
      ],
      cerebroSports: ["eyblwb", "eybl16wb", "eybl15wb", "eybleyclwb"],
    },
  },
};

// Adidas 3SSB — gender splits via byGender so the same body adapts to the
// user's Sport=W vs Sport=M selection. Each age group routes to its own
// Cerebro cache.
const ADIDAS_3SSB_BODY = {
  value: "adidas_3ssb",
  name: "Adidas 3SSB",
  status: "live",
  dataSource: "cerebro",
  byGender: {
    M: {
      subDivisions: [
        { value: "ssb15mb", name: "15U", status: "live", cerebroSports: ["ssb15mb"] },
        { value: "ssb16mb", name: "16U", status: "live", cerebroSports: ["ssb16mb"] },
        { value: "ssb17mb", name: "17U", status: "live", cerebroSports: ["ssb17mb"] },
        { value: "ssbu18mb", name: "Next Generation (U18)", status: "live", cerebroSports: ["ssbu18mb"] },
      ],
      cerebroSports: ["ssb15mb", "ssb16mb", "ssb17mb", "ssbu18mb"],
    },
    F: {
      subDivisions: [
        { value: "ssb15wb", name: "15U Girls", status: "live", cerebroSports: ["ssb15wb"] },
        { value: "ssb16wb", name: "16U Girls", status: "live", cerebroSports: ["ssb16wb"] },
        { value: "ssb17wb", name: "17U Girls", status: "live", cerebroSports: ["ssb17wb"] },
      ],
      cerebroSports: ["ssb15wb", "ssb16wb", "ssb17wb"],
    },
  },
};

// Under Armour Association — Cerebro only carries the boys age groups;
// women's selection returns empty (gracefully) until UA-Girls leagues are
// added to ingest.
const UA_ASSOC_BODY = {
  value: "ua_association",
  name: "Under Armour Association",
  status: "live",
  dataSource: "cerebro",
  byGender: {
    M: {
      subDivisions: [
        { value: "ua15mb", name: "15U", status: "live", cerebroSports: ["ua15mb"] },
        { value: "ua16mb", name: "16U", status: "live", cerebroSports: ["ua16mb"] },
        { value: "ua17mb", name: "17U", status: "live", cerebroSports: ["ua17mb"] },
      ],
      cerebroSports: ["ua15mb", "ua16mb", "ua17mb"],
    },
    F: {
      subDivisions: [
        { value: "ua15wb", name: "Girls 15U", status: "coming" },
        { value: "ua16wb", name: "Girls 16U", status: "coming" },
        { value: "ua17wb", name: "Girls 17U", status: "coming" },
      ],
      cerebroSports: [],
    },
  },
};

// CACHE Partners are NO LONGER defined statically. Each admin-curated
// partner program (Under Armour Next, Adidas Gauntlet, etc.) is injected
// into the Step 4 body list at render time by CacheShareFilters via
// /api/users/cache-teams/partners. Bodies built that way carry
// `cacheLogo: true` so the option renderer shows the CACHE icon next to
// the brand name. See `buildCachePartnerBody` below for the shape.

// Build a virtual governing-body for one CACHE partner program. Lives in
// taxonomy so resolveFetchPlan can recognize the dataSource/partner pair
// without a special case.
export const buildCachePartnerBody = (partner) => ({
  value: `cache_partner_${(partner.partner_program || "").toLowerCase().replace(/[^a-z0-9]+/g, "_")}`,
  name: partner.partner_program || partner.organization || "CACHE Partner",
  status: "live",
  dataSource: "cache_teams",
  partnerProgram: partner.partner_program || partner.organization || "",
  cacheLogo: true,
});

// Any competition from the Cerebro catalogue (the "Find any league" search
// in the cascade). The catalogue key doubles as the cerebroSports key the
// backend resolves on the fly, so no static entry is needed.
export const ANY_LEAGUE_KEY = "cbx_any";
export const ANY_LEAGUE_BODY = {
  value: ANY_LEAGUE_KEY,
  name: "Any league (find the team by name)",
  status: "live",
  dataSource: "cerebro",
  cerebroSports: [ANY_LEAGUE_KEY],
  leagueNames: [],
  byGender: { M: { cerebroSports: [ANY_LEAGUE_KEY] }, F: { cerebroSports: [ANY_LEAGUE_KEY] } },
  anyLeague: true,
};

export const buildCerebroCompetitionBody = (entry) => ({
  value: entry.key,
  name: entry.label || entry.name,
  status: "live",
  dataSource: "cerebro",
  cerebroSports: [entry.key],
  leagueNames: [],
  byGender: {
    M: { cerebroSports: [entry.key] },
    F: { cerebroSports: [entry.key] },
  },
  catalogue: entry,
});

// OTE (Overtime Elite) — paid pre-pro. Athletes carry ote_flag from the
// ingest so downstream CacheNILx™ / CacheShare™ know to apply the
// paid-athlete formula.
const OTE_BODY = {
  value: "ote",
  name: "OTE (Overtime Elite)",
  status: "live",
  dataSource: "cerebro",
  requiresHsGate: true,
  byGender: {
    M: { cerebroSports: ["otemb"] },
    F: { cerebroSports: [] },
  },
};

// Per-US-state federation + classes, extracted directly from the March 2026
// Cerebro events xlsx so Step 5 of State HS Association ("State picker then
// Class/Division for that state") matches what's actually cacheable.
//
// Each entry maps a US state abbreviation to:
//   - state:       full state name as it appears in Cerebro leagueName
//   - acronym:     the federation acronym in parentheses (some are shared:
//                  AHSAA covers AL+AR, IHSAA covers ID+IN, MHSAA covers MI+MS)
//   - classes:     list of class/division strings that appear after the
//                  acronym in the leagueName, used by Step 5b
//
// States not in the xlsx are listed with status "coming" so the picker still
// shows all 50 + DC, but selection returns no teams.
const STATE_HS_DATA = {
  AL: { state: "Alabama", acronym: "AHSAA", classes: ["Class 1A","Class 2A","Class 3A","Class 4A","Class 5A","Class 6A","Class 7A"] },
  AR: { state: "Arkansas", acronym: "AHSAA", classes: ["Class 1A","Class 2A","Class 3A","Class 4A","Class 5A","Class 6A"] },
  CT: { state: "Connecticut", acronym: "CIAC", classes: ["Class L","Class LL","Class M","Class MM","Class S","DIV I","DIV II","DIV III","DIV IV","DIV V"] },
  DC: { state: "District of Columbia", acronym: "DCSAA", classes: ["Class AA"] },
  DE: { state: "Delaware", acronym: "DIAA", classes: [] },
  FL: { state: "Florida", acronym: "FHSAA", classes: ["Class 1A","Class 2A","Class 3A","Class 4A","Class 5A","Class 6A","Class 7A","Class 8A","Class 9A"] },
  GA: { state: "Georgia", acronym: "GHSA", classes: ["Class 1A DIV 1","Class 1A DIV 2","Class 1A Private","Class 1A Public","Class 2A","Class 3A","Class 4A","Class 5A","Class 6A","Class 7A","Class A-Private","Class A-Public","Class Private"] },
  HI: { state: "Hawaii", acronym: "HHSAA", classes: ["DIV 1","DIV 2","DIV I","DIV II"] },
  IA: { state: "Iowa", acronym: "IAHSAA", classes: ["Class 1A","Class 2A","Class 3A","Class 4A","Class 5A"] },
  ID: { state: "Idaho", acronym: "IHSAA", classes: ["Class 1A","Class 1A DI","Class 1A DII","Class 2A","Class 3A","Class 4A","Class 5A","Class 6A"] },
  IL: { state: "Illinois", acronym: "IHSA", classes: ["Class 1A","Class 2A","Class 3A","Class 4A"] },
  IN: { state: "Indiana", acronym: "IHSAA", classes: ["Class 1A","Class 2A","Class 3A","Class 4A"] },
  KS: { state: "Kansas", acronym: "KSHSAA", classes: ["Class 1A-DI","Class 1A-DII","Class 2A","Class 3A","Class 4A","Class 5A","Class 6A"] },
  KY: { state: "Kentucky", acronym: "KHSAA", classes: [] },
  MD: { state: "Maryland", acronym: "MPSSAA", classes: ["Class 1A","Class 2A","Class 3A","Class 4A"] },
  MI: { state: "Michigan", acronym: "MHSAA", classes: ["DIV 1","DIV 2","DIV 3","DIV 4"] },
  MN: { state: "Minnesota", acronym: "MSHSL", classes: ["Class A","Class AA","Class AAA","Class AAAA"] },
  MO: { state: "Missouri", acronym: "MSHSAA", classes: ["Class 1","Class 2","Class 3","Class 4","Class 5","Class 6"] },
  MS: { state: "Mississippi", acronym: "MHSAA", classes: ["Class 1A","Class 2A","Class 3A","Class 4A","Class 5A","Class 6A","Class 7A"] },
  NC: { state: "North Carolina", acronym: "NCHSAA", classes: ["1A","2A","3A","4A"] },
  NE: { state: "Nebraska", acronym: "NSAA", classes: ["Class A","Class B","Class C1","Class C2","Class D1","Class D2"] },
  NJ: { state: "New Jersey", acronym: "NJSIAA", classes: ["Group 1","Group 2","Group 4","Non-Public B"] },
  NM: { state: "New Mexico", acronym: "NMAA", classes: ["Class 5A"] },
  NV: { state: "Nevada", acronym: "NIAA", classes: ["Class 5A"] },
  NY: { state: "NY Catholic", acronym: "CHSAA", classes: ["Class AA"] },
  OH: { state: "Ohio", acronym: "OHSAA", classes: ["DIV 1","DIV 2","DIV 3","DIV 4","DIV 5","DIV 6","DIV 7","DIV I"] },
  OK: { state: "Oklahoma", acronym: "OSSAA", classes: ["Class 2A","Class 3A","Class 4A","Class 5A","Class 6A","Class B"] },
  OR: { state: "Oregon", acronym: "OSAA", classes: ["Class 1A","Class 2A","Class 3A","Class 4A","Class 5A","Class 6A"] },
  PA: { state: "Pennsylvania", acronym: "PIAA", classes: ["Class 1A","Class 2A","Class 3A","Class 4A","Class 5A","Class 6A"] },
  SC: { state: "South Carolina", acronym: "SCHSL", classes: ["Class 1A","Class 2A","Class 3A","Class 4A","Class 5A"] },
  SD: { state: "South Dakota", acronym: "SDHSAA", classes: ["Class A","Class AA","Class B"] },
  TN: { state: "Tennessee", acronym: "TSSAA", classes: ["Class 1A","Class 2A","Class 3A","Class 4A","Class A","Class AA","Class AAA","Class AAAA","DIV 2A","DIV 2AA"] },
  UT: { state: "Utah", acronym: "UHSAA", classes: ["Class 5A","Class 6A"] },
  VA: { state: "Virginia", acronym: "VHSL", classes: ["Class 1A","Class 2A","Class 3A","Class 4A","Class 5A","Class 6A"] },
  WA: { state: "Washington", acronym: "WIAA", classes: ["Class 1A","Class 1B","Class 2A","Class 2B","Class 3A","Class 4A"] },
  WI: { state: "Wisconsin", acronym: "WIAAWI", classes: ["DIV 1","DIV 2","DIV 3","DIV 4","DIV 5"] },
  WV: { state: "West Virginia", acronym: "WVSSAC", classes: ["Class 1A","Class 2A","Class 3A","Class 4A","Class A"] },
  WY: { state: "Wyoming", acronym: "WHSAA", classes: ["Class 1A","Class 2A","Class 3A","Class 4A"] },
};

// 50 states + DC for the State HS Association sub-division picker.
const US_STATES = [
  ['AL','Alabama'],['AK','Alaska'],['AZ','Arizona'],['AR','Arkansas'],
  ['CA','California'],['CO','Colorado'],['CT','Connecticut'],['DE','Delaware'],
  ['DC','District of Columbia'],['FL','Florida'],['GA','Georgia'],['HI','Hawaii'],
  ['ID','Idaho'],['IL','Illinois'],['IN','Indiana'],['IA','Iowa'],
  ['KS','Kansas'],['KY','Kentucky'],['LA','Louisiana'],['ME','Maine'],
  ['MD','Maryland'],['MA','Massachusetts'],['MI','Michigan'],['MN','Minnesota'],
  ['MS','Mississippi'],['MO','Missouri'],['MT','Montana'],['NE','Nebraska'],
  ['NV','Nevada'],['NH','New Hampshire'],['NJ','New Jersey'],['NM','New Mexico'],
  ['NY','New York'],['NC','North Carolina'],['ND','North Dakota'],['OH','Ohio'],
  ['OK','Oklahoma'],['OR','Oregon'],['PA','Pennsylvania'],['RI','Rhode Island'],
  ['SC','South Carolina'],['SD','South Dakota'],['TN','Tennessee'],['TX','Texas'],
  ['UT','Utah'],['VT','Vermont'],['VA','Virginia'],['WA','Washington'],
  ['WV','West Virginia'],['WI','Wisconsin'],['WY','Wyoming'],
];

// Build state subdivision objects per gender. Each carries the federation
// data needed to filter teams to that specific state-acronym pair (handles
// shared acronyms like AHSAA covering both AL and AR), plus the class list
// for the Step 5b Class / Division picker.
const buildStateSubdivision = (abbr, full, gender) => {
  const data = STATE_HS_DATA[abbr];
  const inXlsx = !!data;
  const sport = gender === "F" ? "statehswb" : "statehsmb";
  return {
    value: `statehs_${abbr.toLowerCase()}_${gender.toLowerCase()}`,
    name: `${abbr} ${full}`,
    status: inXlsx ? "live" : "coming",
    cerebroSports: inXlsx ? [sport] : [],
    stateFilter: abbr,
    statePattern: inXlsx ? data.state : null,
    federationAcronym: inXlsx ? data.acronym : null,
    classes: inXlsx ? data.classes : [],
  };
};
const STATE_SUBDIVISIONS_M = US_STATES.map(([a, f]) => buildStateSubdivision(a, f, "M"));
const STATE_SUBDIVISIONS_F = US_STATES.map(([a, f]) => buildStateSubdivision(a, f, "F"));

// State HS Association (50 states + DC). The body itself is gender-aware
// via byGender; each gender exposes the same 51-state picker but routes to
// the matching umbrella cache (statehsmb / statehswb). The picked state's
// `stateFilter` is applied by the cascade UI to narrow team results to that
// state at read time.
const STATE_HS_BODY = {
  value: "state_hs",
  name: "State HS Association",
  status: "live",
  dataSource: "cerebro",
  requiresHsGate: true,
  byGender: {
    M: { cerebroSports: ["statehsmb"], subDivisions: STATE_SUBDIVISIONS_M },
    F: { cerebroSports: ["statehswb"], subDivisions: STATE_SUBDIVISIONS_F },
  },
};

const FIBA_EUROPE_YOUTH_BODY = {
  value: "fiba_europe_youth",
  name: "FIBA Europe Youth",
  status: "coming",
  dataSource: "cerebro",
  // No live keys yet: the youth competitions are not the pro `intl*`
  // umbrellas, so nothing is routed until Cerebro coverage lands.
  cerebroSports: [],
  subDivisions: [
    { value: "u16a", name: "U16 Div A", status: "coming" },
    { value: "u16b", name: "U16 Div B", status: "coming" },
    { value: "u16c", name: "U16 Div C", status: "coming" },
    { value: "u18a", name: "U18 Div A", status: "coming" },
    { value: "u18b", name: "U18 Div B", status: "coming" },
    { value: "u18c", name: "U18 Div C", status: "coming" },
    { value: "u20a", name: "U20 Div A", status: "coming" },
    { value: "u20b", name: "U20 Div B", status: "coming" },
  ],
};

// Single-tier pro leagues: each maps to one cerebro umbrella collection +
// a `leagueNames` filter so the team list comes back scoped to that league.
//
// `mLeagues` / `wLeagues` route the same body to a different leagueName based
// on the user's Sport pick (M vs W). When wLeagues is omitted, women's
// selection of this body returns no results (gender-only league).
const proIntl = (name, mLeagues, wLeagues, status = "live") => ({
  value: name.toLowerCase().replace(/[^a-z0-9]+/g, "_"),
  name,
  status,
  dataSource: "cerebro",
  byGender: {
    M: { cerebroSports: ["intlmb"], leagueNames: mLeagues },
    F: { cerebroSports: ["intlwb"], leagueNames: wLeagues || [] },
  },
});

const proUsa = (value, name, cerebroSport, status = "live") => ({
  value,
  name,
  status,
  dataSource: "cerebro",
  cerebroSports: [cerebroSport],
});

// ---------------------------------------------------------------------------
// GOVERNING_BODIES indexes the entire cascade.
//   GOVERNING_BODIES[tier]                 -> when tier has no region (College)
//   GOVERNING_BODIES[tier][region]         -> Pro / HS / Grassroots
// ---------------------------------------------------------------------------
export const GOVERNING_BODIES = {
  college: {
    list: [
      NCAA_FOOTBALL_BODY,
      NCAA_BODY,
      {
        value: "naia", name: "NAIA", status: "live", dataSource: "cerebro",
        byGender: {
          M: { cerebroSports: ["naiamb"] },
          F: { cerebroSports: ["naiawb"] },
        },
      },
      {
        value: "njcaa", name: "NJCAA", status: "live", dataSource: "cerebro",
        // Cerebro only ingests an NJCAA men's set; women's selection returns
        // empty (gracefully) until an NJCAA-W league is added to ingest.
        byGender: {
          M: { cerebroSports: ["njcaamb"] },
          F: { cerebroSports: [] },
        },
      },
      {
        value: "juco", name: "JUCO", status: "live", dataSource: "cerebro",
        byGender: {
          M: { cerebroSports: ["jucomb"] },
          F: { cerebroSports: ["jucowb"] },
        },
      },
      {
        value: "ccaa", name: "CCAA", status: "live", dataSource: "cerebro",
        byGender: {
          M: { cerebroSports: ["ccaamb"] },
          F: { cerebroSports: ["ccaawb"] },
        },
      },
    ],
  },
  pro: {
    // Per spec Pro — North America: NBA | WNBA | G League | CEBL.
    na: {
      list: [
        proUsa("nba", "NBA", "nbamb"),
        proUsa("wnba", "WNBA", "wnbawb"),
        proUsa("gleague", "G League", "gleaguemb"),
        proIntl("CEBL", ["Canadian Elite Basketball League"]),
      ],
    },
    eu: {
      list: [
        proIntl("EuroLeague", ["EuroLeague"], ["FIBA-W EuroLeague"]),
        proIntl("EuroCup", ["EuroCup"], ["FIBA-W EuroCup"]),
        proIntl("Spanish ACB", ["Spanish ACB"], ["Spanish Womens LF Endesa"]),
        proIntl("German BBL", ["German BBL"], ["German Womens (DBBL)"]),
        proIntl("Turkish BSL", ["Turkish BSL"], ["Turkish Womens Super League (KBSL)"]),
        proIntl(
          "French LNB Pro A",
          ["French Jeep Elite LNB Pro A", "French Betclic Elite LNB Pro A"],
          ["French Womens (LBWL)", "French Womens (LF2)"],
        ),
        proIntl("Italian Lega Basket", ["Italian Lega Basket Serie A"], ["Italian Womens Serie A1"]),
        proIntl("Greek HEBA A1", ["Greek HEBA A1"], ["Greek Womens A1"]),
        proIntl("Lithuanian LKL", ["Lithuanian LKL"]),
        proIntl("Serbian KLS", ["Serbian KLS"]),
        proIntl(
          "Adriatic Liga ABA",
          ["Adriatic League Liga ABA", "Adriatic Liga ABA (2nd DIV)"],
          ["Adriatic Womens (WABA)", "Womens Adriatic Basketball Association (WABA)"],
        ),
        proIntl(
          "Other European",
          [
            "Polish EBL",
            "Czech NBL",
            "Hungarian NBIA",
            "Hungarian Cup",
            "Slovenian SKL",
            "Slovenian Spar Cup",
            "Croatian A-1 Liga (HT Premier League)",
            "Croatian Kresimir Cosic Cup",
            "Adriatic ABA Liga Super Cup",
            "Alpe Adria Cup",
            "Balkan BIBL",
            "Austrian A Bundesliga",
            "Belarusian BPL",
            "Belgium-Scooore League",
            "Bosnian BiH Liga",
            "British BBL",
            "Bulgarian NBL",
            "Cypriot Division A",
            "Danish Basketligaen",
            "Estonian KML (Playoffs)",
            "Estonian-Latvian Basketball League",
            "Finnish Korisliiga",
            "Georgian Super Liga",
            "Kosovo FBK",
            "Lithuanian NKL",
            "Luxembourg Total League",
            "Macedonian Superleague",
            "Montenegrin Prva A Liga",
            "Netherlands DBL",
            "Norwegian BLNO",
            "Portuguese LPB",
            "Romanian Divizia A",
            "Russian VTB SuperCup",
            "Russian VTB United League",
            "Slovakian Extraliga",
            "Swedish Basketligan",
            "Swiss LNA",
            "Ukrainian Superleague",
            "Basketball Champions League (Europe)",
            "Basketball Champions League (Qualifiers)",
            "Basketball Super League",
            "FIBA Europe Cup",
            "FIBA Europe Cup (Qualifiers)",
            "FIBA European Championship for Small Countries",
            "German Cup - BBL Pokal",
            "German Pro A",
            "German Pro B",
            "Italian Cup",
            "Italian Serie A2 Basket",
            "Italian Serie B Basket",
            "Greek HEBA A2",
            "Turkish Cup (TBF)",
            "Turkish TBL",
          ],
          [
            "Polish Womens (OBLK)",
            "Czech Republic Womens (ZBL)",
            "Hungarian Womens NB I. A",
            "Slovenian Womens (SKL)",
            "Serbian Womens 1. ZLS",
          ],
        ),
      ],
    },
    // Per spec Pro — Asia-Pacific.
    ap: {
      list: [
        proIntl("Chinese CBA", ["Chinese CBA"], ["Chinese Womens (WCBA)"]),
        proIntl("South Korean KBL", ["South Korean KBL"]),
        proIntl("Japanese B.League", ["Japanese B.League"], ["Japanese Womens (WJBL)"]),
        proIntl("Australian NBL", ["Australian NBL"], ["Australian Womens (WNBL)"]),
        proIntl("PBA (Philippines)", ["PBA - Philippines"]),
        proIntl("Taiwanese P.League", ["Taiwanese P.League"], ["Taiwanese Womens (WSBL)"]),
        proIntl(
          "Other Asia-Pac",
          [
            "New Zealand NBL",
            "Taiwanese T1 League",
            "Japanese JBL",
            "Australian NBL Blitz",
            "Australian NBL1",
            "Australian NBL1 - Wildcard",
            "Basketball Champions League (Asia)",
            "East Asia Super League",
            "FIBA Asia",
            "FIBA Pacific Games",
            "FIBA Oceania",
          ],
        ),
      ],
    },
    // Per spec Pro — Americas (LatAm).
    lat: {
      list: [
        proIntl("Brazilian NBB", ["Brazilian NBB"]),
        proIntl("Argentinian Liga A", ["Argentinian Liga A"], ["Argentinian Womens La Liga"]),
        proIntl("Mexican LNBP", ["Mexican LNBP"]),
        proIntl("Venezuelan SLB", ["Venezuelan SLB"]),
        proIntl("Puerto Rican BSN", ["Puerto Rican BSN"]),
        proIntl("Liga Sudamericana", ["Liga Sudamericana"], ["Liga Sudamericana de Clubes Femenina"]),
        proIntl(
          "Other LatAm",
          [
            "Liga Uruguaya De Basquetbol",
            "Liga Uruguaya De Basquetbol (Playoffs)",
            "Mexican CIBACOPA",
            "Canadian NBL",
            "Basketball Champions League (Americas)",
            "FIBA Centrobasket",
            "FIBA South America",
          ],
          [
            "Womens Basketball League (Americas)",
            "FIBA-W CBC",
            "FIBA-W COCABA",
            "FIBA-W Centrobasket",
            "FIBA-W South America",
          ],
        ),
      ],
    },
    // Per spec Pro — Africa / MENA.
    amena: {
      list: [
        proIntl("Basketball Africa League", ["Basketball Africa League"]),
        proIntl("Israeli BSL", ["Israeli BSL"], ["Israeli Womens (WBL)"]),
        proIntl("Lebanese Division A", ["Lebanese Division A"]),
        proIntl(
          "Other Africa/MENA",
          [
            "West Asia Super League",
            "West Asia Super League - Gulf",
            "West Asia Super League - West Asia",
            "Africa Champions Clubs ROAD TO BAL",
            "Basketball Africa League (Qualifiers)",
            "FIBA AfroCan",
            "FIBA AfroCan (Qualifiers)",
            "Israel BSL Winner Cup",
          ],
          [
            "FIBA-W Africa Basketball League",
            "FIBA-W Africa Champions Cup",
          ],
        ),
      ],
    },
    // Spec doesn't list Pro leagues for Oceania, but Step 3 includes
    // Oceania as a region. Surface the natural fits (AUS / NZ NBL).
    oce: {
      list: [
        proIntl("Australian NBL", ["Australian NBL"], ["Australian Womens (WNBL)"]),
        proIntl("New Zealand NBL", ["New Zealand NBL"]),
      ],
    },
  },
  hs: (() => {
    // Per spec HS / Grassroots — International (all regions): the same 6
    // items appear in every non-NA region.
    const hsIntlBodies = [
      // FIBA Europe Youth has sub-divisions wired up; the others are coming.
      FIBA_EUROPE_YOUTH_BODY,
      { value: "fiba_americas_youth", name: "FIBA Americas Youth", status: "coming", dataSource: "cerebro" },
      { value: "fiba_asia_youth", name: "FIBA Asia Youth", status: "coming", dataSource: "cerebro" },
      { value: "fiba_africa_youth", name: "FIBA Africa Youth", status: "coming", dataSource: "cerebro" },
      { value: "fiba_oceania_youth", name: "FIBA Oceania Youth", status: "coming", dataSource: "cerebro" },
      { value: "natl_youth_fed", name: "National Youth Federation", status: "coming", dataSource: "cerebro" },
    ];
    return {
      // Per spec HS / Grassroots — North America.
      na: {
        list: [
          EYBL_BODY,
          ADIDAS_3SSB_BODY,
          UA_ASSOC_BODY,
          OTE_BODY,
          STATE_HS_BODY,
          { value: "elite_showcase", name: "Elite Showcase", status: "coming", dataSource: "cerebro" },
          { value: "scouting_exposure", name: "Scouting / Exposure", status: "coming", dataSource: "cerebro" },
        ],
      },
      eu: { list: hsIntlBodies },
      ap: { list: hsIntlBodies },
      lat: { list: hsIntlBodies },
      amena: { list: hsIntlBodies },
      oce: { list: hsIntlBodies },
    };
  })(),
};

// True when the governing body has a real cache routing for the requested
// gender. Used to hide men-only bodies (NBA, G League) when the user picks
// Women/Girls and women-only bodies (WNBA) when the user picks Men/Boys.
//
// Bodies whose status is "coming" return true so the dropdown still shows
// them as available / disabled-with-Soon.
export const supportsGender = (body, gender) => {
  if (!body) return false;
  if (!gender) return true;
  if (body.status === "coming") return true;
  if (body.dataSource === "sportsradar") return true;
  // Genius Sports bodies (NCAA Football) belong to gender-less sports —
  // the cascade never asks for a gender, so nothing to filter on.
  if (body.dataSource === "genius") return true;
  // CACHE Partners always supports both genders — the team list comes back
  // gender-filtered from the backend (only teams whose stored `gender`
  // matches surface).
  if (body.dataSource === "cache_teams") return true;
  if (body.byGender) {
    const branch = body.byGender[gender];
    return !!(
      branch &&
      ((branch.cerebroSports && branch.cerebroSports.length) ||
        (branch.leagueNames && branch.leagueNames.length))
    );
  }
  if (body.cerebroSports && body.cerebroSports.length) {
    // Infer gender from the cache sport-key suffix:
    //   ...mb (Men's basketball) -> M
    //   ...wb (Women's basketball) -> F
    const wantsF = gender === "F";
    return body.cerebroSports.every((s) => {
      if (s.endsWith("wb")) return wantsF;
      if (s.endsWith("mb")) return !wantsF;
      return true;
    });
  }
  return true;
};

// True when a governing body belongs to the picked sport. Bodies carry an
// optional `sports` array ("fb", "bb", ...); the pre-football bodies have no
// tag and default to basketball-only, so existing basketball callers (and
// CacheShareFilters, which never passes a sport) keep their exact behavior.
export const supportsSport = (body, sportValue) =>
  (body?.sports || ["bb"]).includes(sportValue || "bb");

// Get the governing-body list for a (tier, region), filtered to the picked
// sport + gender so the dropdown never shows leagues the user can't actually
// pick. When `region` is omitted for Pro/HS (athlete doesn't know it), unions
// every region's bodies under the tier — same shape as picking each region
// in turn — and de-dupes by `value` so no body appears twice.
export const governingBodiesFor = ({ tier, region, gender, sport }) => {
  if (!tier) return [];
  let list;
  if (tier === "college") {
    list = GOVERNING_BODIES.college.list || [];
  } else {
    const branch = GOVERNING_BODIES[tier];
    if (!branch) return [];
    if (region) {
      list = branch[region]?.list || [];
    } else {
      const seen = new Set();
      list = [];
      for (const r of Object.values(branch)) {
        for (const b of (r?.list || [])) {
          if (b?.value && !seen.has(b.value)) {
            seen.add(b.value);
            list.push(b);
          }
        }
      }
    }
  }
  return list.filter(
    (b) => supportsSport(b, sport) && supportsGender(b, gender),
  );
};

// State HS associations, loaded from Cerebro's competition catalogue
// (/cerebro/competitions/states/) so the picker lists every state that has
// association competitions, with each state's real classes. Each state
// carries catalogue keys, so a pick lists exactly those competitions'
// teams and needs no client-side name filtering. The hand-typed
// STATE_HS_DATA table stays as the fallback until the fetch completes or
// when it fails.
const STATE_ASSOCIATIONS = { M: null, F: null };
const STATE_ASSOCIATION_LOADS = { M: null, F: null };
const US_STATE_NAMES = Object.fromEntries(US_STATES);

const stateSubdivisionFromCatalogue = (row, gender) => ({
  value: `statehs_${row.abbr.toLowerCase()}_${gender.toLowerCase()}`,
  name: `${row.abbr} ${row.name || US_STATE_NAMES[row.abbr] || row.abbr}`,
  status: "live",
  dynamic: true,
  cerebroSports: row.cerebroSports || [],
  stateFilter: row.abbr,
  statePattern: null,
  federationAcronym: null,
  acronyms: row.acronyms || [],
  classes: (row.classes || []).map((c) => ({ name: c.name, cerebroSports: c.cerebroSports || [] })),
});

export const loadStateAssociations = (gender) => {
  const g = gender === "F" ? "F" : "M";
  if (STATE_ASSOCIATIONS[g]) return Promise.resolve(STATE_ASSOCIATIONS[g]);
  if (!STATE_ASSOCIATION_LOADS[g]) {
    STATE_ASSOCIATION_LOADS[g] = import("./services/cacheShareServices")
      .then(({ fetchCerebroStates }) => fetchCerebroStates(g))
      .then((rows) => {
        const live = (rows || []).map((r) => stateSubdivisionFromCatalogue(r, g));
        const covered = new Set(live.map((r) => r.stateFilter));
        // Keep the 50 states + DC visible: states Cerebro has nothing for
        // show as "coming soon" instead of disappearing.
        const missing = US_STATES
          .filter(([abbr]) => !covered.has(abbr))
          .map(([abbr, full]) => ({
            value: `statehs_${abbr.toLowerCase()}_${g.toLowerCase()}`,
            name: `${abbr} ${full}`,
            status: "coming",
            dynamic: true,
            cerebroSports: [],
            stateFilter: abbr,
            classes: [],
          }));
        STATE_ASSOCIATIONS[g] = [...live, ...missing].sort((a, b) => a.name.localeCompare(b.name));
        return STATE_ASSOCIATIONS[g];
      })
      .catch(() => {
        STATE_ASSOCIATION_LOADS[g] = null;
        return null;
      });
  }
  return STATE_ASSOCIATION_LOADS[g];
};

export const stateAssociationsLoaded = (gender) => !!STATE_ASSOCIATIONS[gender === "F" ? "F" : "M"];

// Resolve the sub-division list for a governing body, honoring the gender
// branch when present (EYBL splits by gender). Returns [] when the body has
// no sub-divisions (e.g. NBA, EuroLeague). `gender` is "M" | "F" | null.
export const subDivisionsFor = ({ body, gender }) => {
  if (!body) return [];
  if (body.value === STATE_HS_BODY.value && gender && STATE_ASSOCIATIONS[gender]) {
    return STATE_ASSOCIATIONS[gender];
  }
  if (body.byGender && gender && body.byGender[gender]) {
    return body.byGender[gender].subDivisions || [];
  }
  return body.subDivisions || [];
};

// Class / division options for a picked sub-division. Catalogue-driven
// states carry {name, cerebroSports}; the legacy table carries plain strings.
export const classOptionsFor = (subDivision) =>
  ((subDivision && subDivision.classes) || []).map((c) =>
    (typeof c === "string" ? { name: c, cerebroSports: null } : { name: c.name, cerebroSports: c.cerebroSports || null }));

// ---------------------------------------------------------------------------
// Admin tree.
//
// Walks GOVERNING_BODIES so every league surfaced in the user-facing cascade
// also appears in the admin Data Sync view, grouped by Tier > Region > Body.
//
// Card shape (one card per backend cache collection — Run/Stop per card):
//   {
//     source: "cb" | "sr",
//     sportKey: "intlmb",                  -- backend cache key
//     gender: "M" | "F" | null,            -- which gender pulled this card
//     label: "International (Men)",        -- user-readable card title
//     coverage: ["EuroLeague", ...],       -- leagueNames inside this card
//   }
//
// Multiple bodies that share the same sport-key (e.g. EuroLeague + Spanish
// ACB both live in intlmb) get merged into a single card with all their
// `leagueNames` unioned into `coverage`, so admin sees the full picture
// without misleading per-league Run buttons that don't exist.
// ---------------------------------------------------------------------------

const SR_CARDS_BY_GENDER = {
  M: { source: "sr", sportKey: "mbb", gender: "M", label: "NCAA Men's Basketball" },
  F: { source: "sr", sportKey: "wbb", gender: "F", label: "NCAA Women's Basketball" },
};

// Pretty label for a Cerebro sport-key. Falls back to the key itself.
const CB_LABEL_BY_KEY = {
  nbamb: "NBA",
  wnbawb: "WNBA",
  gleaguemb: "NBA G-League",
  intlmb: "International (Men)",
  intlwb: "International (Women)",
  eyblmb: "EYBL Boys 17U",
  eyblwb: "EYBL Girls 17U",
  eybl16mb: "EYBL Boys 16U",
  eybl16wb: "EYBL Girls 16U",
  eybl15mb: "EYBL Boys 15U",
};

const cbLabel = (key) => CB_LABEL_BY_KEY[key] || key;

// Walk one (tier, region) and emit a list of bodies, each with merged cards.
const cardsForRegion = (bodies) => {
  const grouped = []; // [{bodyLabel, cards: [card, ...]}]
  for (const body of bodies || []) {
    if (body.status === "coming") continue;
    const cards = [];
    const addCard = ({ source, sportKey, gender, leagueNames }) => {
      if (!sportKey) return;
      const existing = cards.find(
        (c) => c.source === source && c.sportKey === sportKey,
      );
      const coverage = leagueNames || [];
      if (existing) {
        for (const ln of coverage) {
          if (!existing.coverage.includes(ln)) existing.coverage.push(ln);
        }
      } else {
        cards.push({
          source,
          sportKey,
          gender: gender || null,
          label: source === "sr"
            ? SR_CARDS_BY_GENDER[gender]?.label || sportKey
            : cbLabel(sportKey),
          coverage: [...coverage],
        });
      }
    };

    if (body.dataSource === "sportsradar") {
      // NCAA: one SR card per gender.
      addCard({ source: "sr", sportKey: "mbb", gender: "M" });
      addCard({ source: "sr", sportKey: "wbb", gender: "F" });
    } else if (body.dataSource === "cerebro") {
      if (body.byGender) {
        for (const g of ["M", "F"]) {
          const branch = body.byGender[g];
          const sports = branch?.cerebroSports || [];
          for (const sk of sports) {
            addCard({
              source: "cb",
              sportKey: sk,
              gender: g,
              leagueNames: branch?.leagueNames || [],
            });
          }
        }
      } else if (body.cerebroSports?.length) {
        for (const sk of body.cerebroSports) {
          // Infer gender from the cache-key suffix.
          const g = sk.endsWith("wb") ? "F" : sk.endsWith("mb") ? "M" : null;
          addCard({
            source: "cb",
            sportKey: sk,
            gender: g,
            leagueNames: body.leagueNames || [],
          });
        }
      }
    }
    if (cards.length) grouped.push({ bodyLabel: body.name, cards });
  }
  return grouped;
};

// Build the admin cascade tree. Returns:
//   [
//     { tier, tierLabel, regions: [
//       { regionKey, regionLabel, bodies: [
//         { bodyLabel, cards: [{source, sportKey, label, coverage}] }
//       ] }
//     ] }
//   ]
export const buildAdminTree = () => {
  const tree = [];

  tree.push({
    tier: "college",
    tierLabel: "College",
    regions: [
      {
        regionKey: null,
        regionLabel: null,
        bodies: cardsForRegion(GOVERNING_BODIES.college.list),
      },
    ],
  });

  const proRegions = [];
  for (const region of REGIONS_BY_TIER.pro) {
    const bodies = cardsForRegion(GOVERNING_BODIES.pro[region.value]?.list);
    if (bodies.length) {
      proRegions.push({
        regionKey: region.value,
        regionLabel: region.name,
        bodies,
      });
    }
  }
  tree.push({ tier: "pro", tierLabel: "Pro", regions: proRegions });

  const hsRegions = [];
  for (const region of REGIONS_BY_TIER.hs) {
    const bodies = cardsForRegion(GOVERNING_BODIES.hs[region.value]?.list);
    if (bodies.length) {
      hsRegions.push({
        regionKey: region.value,
        regionLabel: region.name,
        bodies,
      });
    }
  }
  tree.push({ tier: "hs", tierLabel: "HS / Grassroots", regions: hsRegions });

  return tree;
};

// Resolve the cache fetch plan for the current cascade selection.
//
// Returns a single object describing how the team picker should be loaded:
//   {
//     dataSource: "cerebro" | "sportsradar",
//     cerebroSports: ["nbamb", ...],   // for cerebro plans
//     leagueNames: ["EuroLeague"],     // optional umbrella scoping
//     srSport:    "mbb",               // for sportsradar plans
//     conference: "ACC" | null,        // applied client-side after fetch
//   }
//
// `null` means the cascade has not yet selected enough to fetch teams.
export const resolveFetchPlan = ({
  sportValue,
  gender,
  tier,
  region,
  body,
  // Athlete onboarding has no League step, so it opts into the College
  // skip-body union (search across NCAA/NAIA/NJCAA/... at once). CacheShare
  // and coach flows keep requiring an explicit body pick.
  collegeUnion = false,
  subDivision,
  classDivision,
  conference,
  // Team name typed in Step 7; only the "Any league" scope sends it to the
  // server (every other scope filters the fetched list client-side).
  teamSearch = "",
}) => {
  if (!sportValue || !tier) return null;
  // Gender is required for any sport that has a gender split. Without it,
  // the cascade can't pick the right cache (mbb vs wbb, intlmb vs intlwb).
  const resolvedGender =
    gender || cerebroGenderForSport(sportValue) || null;
  if (!resolvedGender && sportValue !== "fb") return null;

  // College tier requires an explicit governing-body pick. NCAA / NAIA /
  // NJCAA / JUCO / CCAA address different data sources and roster shapes;
  // the "all of college" union we built earlier turned out to be more
  // confusing than useful (sub-bodies overlap and the result list is too
  // broad to act on). Pro and HS still allow the skip-body union below.
  if (tier === "college" && !body && !collegeUnion) {
    return null;
  }

  // No governing body picked → "search across all of {tier, region}".
  // Union sources from every body that supports the picked gender at this
  // tier / region. The team picker fans out in parallel, dedupes by team
  // id, and presents one combined list.
  //
  // SR-backed bodies (NCAA D-I men's / women's basketball) contribute an
  // `srSport` plan; Cerebro-backed bodies contribute cerebroSports +
  // leagueNames. NCAA's D-II / D-III sub-divisions also contribute their
  // own Cerebro sport keys so a College-wide picker shows those teams
  // even when the user skips the body selector.
  if (!body) {
    let bodies;
    if (tier === "college") {
      bodies = (GOVERNING_BODIES.college?.list || []);
    } else if (region) {
      bodies = (GOVERNING_BODIES[tier]?.[region]?.list || []);
    } else {
      // Tier picked but region skipped — union every region under the tier.
      const branch = GOVERNING_BODIES[tier] || {};
      bodies = Object.values(branch).flatMap((r) => r?.list || []);
    }
    const sportsSet = new Set();
    const namesSet = new Set();
    const srSports = new Set();
    let geniusSport = null;
    for (const b of bodies) {
      // Coming-soon bodies have no live routing; unioning their placeholder
      // keys would pour unrelated leagues into the tier (the FIBA youth
      // placeholder once dragged every international pro team into HS).
      if (b.status === "coming") continue;
      if (!supportsSport(b, sportValue)) continue;
      if (!supportsGender(b, resolvedGender)) continue;

      // Genius Sports (NCAA Football): one live plan covers every team.
      if (b.dataSource === "genius") {
        geniusSport = sportValue;
        continue;
      }

      if (b.dataSource === "sportsradar") {
        // Only NCAA today. Derive srSport from gender the same way the
        // body-picked branch does.
        if (b.value === "ncaa") {
          srSports.add(resolvedGender === "F" ? "wbb" : "mbb");
        }
        // Walk sub-divisions for Cerebro overrides (D-II / D-III) so the
        // skip-body picker still surfaces those teams. SR-only subs
        // contribute nothing here — they're already covered by the
        // parent SR fetch.
        for (const sub of b.subDivisions || []) {
          const subBranch = sub.byGender?.[resolvedGender];
          const subSports = subBranch?.cerebroSports?.length
            ? subBranch.cerebroSports
            : (sub.cerebroSports || []);
          for (const s of subSports) sportsSet.add(s);
        }
        continue;
      }

      // Cerebro body
      const branch = b.byGender?.[resolvedGender];
      const bodySports = branch?.cerebroSports?.length
        ? branch.cerebroSports
        : (b.cerebroSports || []);
      const bodyNames = branch?.leagueNames?.length
        ? branch.leagueNames
        : (b.leagueNames || []);
      for (const s of bodySports) sportsSet.add(s);
      for (const n of bodyNames) namesSet.add(n);
    }

    const subPlans = [];
    if (geniusSport) {
      subPlans.push({ dataSource: "genius", sport: geniusSport, gender: resolvedGender });
    }
    for (const srSport of srSports) {
      subPlans.push({
        dataSource: "sportsradar",
        srSport,
        conference: null,
        conferenceFilter: null,
        subDivision: null,
        gender: resolvedGender,
      });
    }
    if (sportsSet.size || namesSet.size) {
      subPlans.push({
        dataSource: "cerebro",
        cerebroSports: Array.from(sportsSet),
        leagueNames: namesSet.size ? Array.from(namesSet) : null,
        gender: resolvedGender,
      });
    }
    // CACHE Partner teams are admin-curated outside the static taxonomy,
    // so the body union above never picks them up. Always append a
    // cache_teams sub-plan for HS so a user who skips the League step
    // (or just types a team name in Step 7) still sees All In Elite /
    // every other CACHE partner team alongside Cerebro + SR results.
    // The endpoint returns an empty list when nothing is curated, so the
    // sub-plan is free when admins haven't uploaded anything yet.
    if (tier === "hs" && (sportValue || "bb") === "bb") {
      subPlans.push({
        dataSource: "cache_teams",
        gender: resolvedGender,
      });
    }
    if (!subPlans.length) return null;
    if (subPlans.length === 1) return subPlans[0];
    return {
      dataSource: "multi",
      plans: subPlans,
      gender: resolvedGender,
    };
  }

  // CACHE Partners — admin-managed teams. One cascade body per
  // partner_program (Under Armour Next, etc.). The plan carries the
  // partner so executeFetchPlan can narrow the team list.
  if (body.dataSource === "cache_teams") {
    return {
      dataSource: "cache_teams",
      gender: resolvedGender,
      partner: body.partnerProgram || null,
    };
  }

  // Genius Sports bodies (NCAA Football). Teams + rosters + season stats
  // come live from the Genius Warehouse REST API; the backend unions every
  // current competition and tags each team with its competitionId, so the
  // plan needs no further narrowing here.
  if (body.dataSource === "genius") {
    return {
      dataSource: "genius",
      sport: sportValue,
      gender: resolvedGender,
    };
  }

  // Sub-division can override the parent body's dataSource. NCAA's D-II /
  // D-III sub-divisions, for example, are SR-naïve but route through new
  // Cerebro caches (ncaad2mb / ncaad3mb / wb). Conference picks can also
  // override: a D-II HBCU conference (CIAA, SIAC) under NCAA D-I should
  // route to the D-II cache, not SR D-I where it'd return zero teams.
  const subBranchPre =
    subDivision?.byGender && resolvedGender
      ? subDivision.byGender[resolvedGender]
      : null;
  const subOverridesToCerebro = !!(
    subBranchPre?.cerebroSports?.length ||
    subDivision?.cerebroSports?.length
  );
  const confMeta = conferenceMeta(conference);
  const conferenceForcesD2 = confMeta?.division === "d2";
  const conferenceForcesD3 = confMeta?.division === "d3";

  if (
    body.dataSource === "sportsradar" &&
    !subOverridesToCerebro &&
    !conferenceForcesD2 &&
    !conferenceForcesD3
  ) {
    if (body.value !== "ncaa") return null;
    const srSport = resolvedGender === "F" ? "wbb" : "mbb";
    // Explicit conference pick wins over a sub-division's bucket filter,
    // matching the Cerebro branch's precedence. P4 / G5 / HBCU sub-divisions
    // forward their conferenceFilter array so the executor can narrow the
    // SR team list to those conferences (e.g. P4 -> just SEC / Big Ten / etc).
    const conferenceList = conference
      ? [conference]
      : (subDivision?.conferenceFilter && subDivision.conferenceFilter.length
          ? subDivision.conferenceFilter
          : null);
    return {
      dataSource: "sportsradar",
      srSport,
      conference: conference || null,
      conferenceFilter: conferenceList,
      subDivision: subDivision?.value || null,
      gender: resolvedGender,
    };
  }

  // Auto-route to NCAA D-II / D-III Cerebro caches when the picked conference
  // belongs to that division (CIAA / SIAC are D-II HBCU; spec lists them in
  // the same Step 6 group as MEAC / SWAC for UX). The Cerebro D-II/D-III
  // team docs don't yet carry a conference field, so we surface the matching
  // teams (better than zero) and the cascade will tighten the filter once
  // Cerebro exposes conference metadata.
  if (body.value === "ncaa" && (conferenceForcesD2 || conferenceForcesD3)) {
    const sportKey = conferenceForcesD2
      ? (resolvedGender === "F" ? "ncaad2wb" : "ncaad2mb")
      : (resolvedGender === "F" ? "ncaad3wb" : "ncaad3mb");
    return {
      dataSource: "cerebro",
      cerebroSports: [sportKey],
      leagueNames: null,
      conferenceHint: conference || null,
      gender: resolvedGender,
    };
  }

  if (body.dataSource === "cerebro") {
    const branch =
      body.byGender && resolvedGender ? body.byGender[resolvedGender] : null;
    const subBranch =
      subDivision?.byGender && resolvedGender
        ? subDivision.byGender[resolvedGender]
        : null;
    let cerebroSports;
    if (subBranch?.cerebroSports?.length) {
      cerebroSports = subBranch.cerebroSports;
    } else if (subDivision?.cerebroSports?.length) {
      cerebroSports = subDivision.cerebroSports;
    } else if (branch?.cerebroSports?.length) {
      cerebroSports = branch.cerebroSports;
    } else if (body.cerebroSports?.length) {
      cerebroSports = body.cerebroSports;
    } else {
      return null;
    }
    const leagueNames =
      subBranch?.leagueNames ||
      subDivision?.leagueNames ||
      branch?.leagueNames ||
      body.leagueNames ||
      null;
    // Conference narrowing — applied client-side in executeFetchPlan
    // against the `conference` field on each Cerebro team. Single-pick
    // (Step 6 conference chip) wins over a sub-division's broader
    // `conferenceFilter` array, since the user's explicit pick is more
    // specific than the bucket-level NCAA P4/G5/HBCU narrowing.
    const conferenceList =
      conference
        ? [conference]
        : (subDivision?.conferenceFilter && subDivision.conferenceFilter.length
            ? subDivision.conferenceFilter
            : null);
    // Catalogue-driven states: the state (or the picked class) carries the
    // exact competition keys, so the server returns just those teams and
    // the client-side name filters below stay off.
    const catalogueClass = classDivision?.cerebroSports?.length ? classDivision.cerebroSports : null;
    if (catalogueClass) cerebroSports = catalogueClass;
    const catalogueState = !!subDivision?.dynamic;
    return {
      dataSource: "cerebro",
      cerebroSports,
      leagueNames: leagueNames && leagueNames.length ? leagueNames : null,
      anyLeague: body.value === ANY_LEAGUE_KEY || undefined,
      teamQuery: body.value === ANY_LEAGUE_KEY ? (teamSearch || "").trim() : undefined,
      stateFilter: catalogueState ? null : (subDivision?.stateFilter || null),
      statePattern: catalogueState ? null : (subDivision?.statePattern || null),
      federationAcronym: catalogueState ? null : (subDivision?.federationAcronym || null),
      classFilter: catalogueState || catalogueClass ? null : (classDivision?.value || null),
      conferenceFilter: conferenceList,
      gender: resolvedGender,
    };
  }

  return null;
};
